import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Navigate } from 'react-router';
import { httpsCallable } from 'firebase/functions';
import { ADMIN_EMAILS } from '@erectus/shared';
import type { AdminDay, AdminFunnel, AdminGa, AdminGaRequest, AdminOverview, AdminRange, AdminUserRow } from '@erectus/shared';
import { Spinner } from '@/ui/primitives.js';
import { useAuth } from '@/stores/auth.js';
import { firebase } from '@/lib/firebase.js';

/**
 * /admin — the team's dashboard. Reached only by typing the URL; the page
 * hides itself from anyone not on ADMIN_EMAILS, and the functions behind it
 * refuse them regardless. Laid out as a web page, not the phone shell: it
 * scrolls, and spreads out on a desktop.
 */
export function isAdminEmail(email: string | null | undefined): boolean {
  return Boolean(email && ADMIN_EMAILS.includes(email));
}

// ── ranges ───────────────────────────────────────────────────────────────────

type Preset = 'today' | 'yesterday' | 'thisWeek' | 'lastWeek' | 'last7' | 'last28' | 'thisMonth' | 'lastMonth' | 'last90' | 'custom';
const PRESETS: Array<{ id: Preset; label: string }> = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'thisWeek', label: 'This week' },
  { id: 'lastWeek', label: 'Last week' },
  { id: 'last7', label: 'Last 7 days' },
  { id: 'last28', label: 'Last 28 days' },
  { id: 'thisMonth', label: 'This month' },
  { id: 'lastMonth', label: 'Last month' },
  { id: 'last90', label: 'Last 90 days' },
  { id: 'custom', label: 'Custom' },
];

const midnight = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d: Date, n: number): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const iso = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const fromIso = (s: string): Date => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y!, m! - 1, d!);
};

/** Inclusive first and last day (local) for a preset. */
function presetDays(preset: Preset, custom: { from: string; to: string }): { from: Date; to: Date } {
  const today = midnight(new Date());
  const weekStart = addDays(today, -today.getDay());
  switch (preset) {
    case 'today':
      return { from: today, to: today };
    case 'yesterday':
      return { from: addDays(today, -1), to: addDays(today, -1) };
    case 'thisWeek':
      return { from: weekStart, to: today };
    case 'lastWeek':
      return { from: addDays(weekStart, -7), to: addDays(weekStart, -1) };
    case 'last7':
      return { from: addDays(today, -6), to: today };
    case 'last28':
      return { from: addDays(today, -27), to: today };
    case 'thisMonth':
      return { from: new Date(today.getFullYear(), today.getMonth(), 1), to: today };
    case 'lastMonth':
      return { from: new Date(today.getFullYear(), today.getMonth() - 1, 1), to: new Date(today.getFullYear(), today.getMonth(), 0) };
    case 'last90':
      return { from: addDays(today, -89), to: today };
    case 'custom':
      return { from: fromIso(custom.from), to: fromIso(custom.to) };
  }
}

// ── page ─────────────────────────────────────────────────────────────────────

export function AdminPage(): ReactNode {
  const { user, loading } = useAuth();
  const admin = isAdminEmail(user?.email);
  const [preset, setPreset] = useState<Preset>('last28');
  const [custom, setCustom] = useState(() => ({ from: iso(addDays(new Date(), -13)), to: iso(new Date()) }));
  const [compare, setCompare] = useState(true);
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [ga, setGa] = useState<AdminGa | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [gaError, setGaError] = useState<string | null>(null);

  const days = useMemo(() => presetDays(preset, custom), [preset, custom]);
  const range: AdminRange = useMemo(() => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return { start: days.from.getTime(), end: addDays(days.to, 1).getTime(), tz };
  }, [days]);
  const length = Math.round((range.end - range.start) / 86_400_000);

  useEffect(() => {
    if (!admin || !(range.start < range.end)) return;
    let cancelled = false;
    setOverview(null);
    setGa(null);
    setError(null);
    setGaError(null);
    const fns = firebase().functions;
    void httpsCallable<AdminRange, AdminOverview>(fns, 'adminOverview')(range)
      .then((r) => !cancelled && setOverview(r.data))
      .catch((e: unknown) => !cancelled && setError(e instanceof Error ? e.message : String(e)));
    // GA gets the same instants; the server maps them onto the property's
    // reporting days and labels the rows with this viewer's days.
    void httpsCallable<AdminGaRequest, AdminGa>(fns, 'adminGa')(range)
      .then((r) => !cancelled && setGa(r.data))
      .catch((e: unknown) => !cancelled && setGaError(e instanceof Error ? e.message : String(e)));
    return () => {
      cancelled = true;
    };
  }, [admin, range]);

  if (loading) {
    return (
      <div className="grid min-h-dvh place-items-center bg-surface">
        <Spinner label="Checking access" />
      </div>
    );
  }
  if (!admin) return <Navigate to="/progress" replace />;

  const fmt = (d: Date): string => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  const prevFrom = addDays(days.from, -length);
  const prevTo = addDays(days.from, -1);

  return (
    <div className="selectable fixed inset-0 overflow-y-auto bg-surface text-ink">
      <div className="mx-auto max-w-[1400px] space-y-6 px-4 pb-16 pt-[calc(1.25rem+env(safe-area-inset-top))] md:px-8">
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted">Erectus · team only</p>
            <h1 className="text-2xl font-bold md:text-3xl">Admin</h1>
            <p className="text-sm text-muted">
              {fmt(days.from)} – {fmt(days.to)} ({length} day{length === 1 ? '' : 's'})
              {compare ? ` · vs ${fmt(prevFrom)} – ${fmt(prevTo)}` : ''}
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="flex flex-wrap justify-end gap-1.5">
              {PRESETS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPreset(p.id)}
                  className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                    preset === p.id ? 'border-ink bg-ink text-white' : 'border-line bg-white text-ink-soft hover:border-ink/40'
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              {preset === 'custom' ? (
                <>
                  <input
                    id="admin-from"
                    type="date"
                    value={custom.from}
                    max={custom.to}
                    onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value || c.from }))}
                    className="rounded-lg border border-line bg-white px-2 py-1"
                  />
                  <span>to</span>
                  <input
                    id="admin-to"
                    type="date"
                    value={custom.to}
                    min={custom.from}
                    onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value || c.to }))}
                    className="rounded-lg border border-line bg-white px-2 py-1"
                  />
                </>
              ) : null}
              <label className="flex items-center gap-1.5 font-medium">
                <input id="admin-compare" type="checkbox" checked={compare} onChange={(e) => setCompare(e.target.checked)} />
                Compare with the previous {length} day{length === 1 ? '' : 's'}
              </label>
            </div>
          </div>
        </header>

        {error ? <Panel className="text-sm text-[color:var(--color-state-slouch)]">{error}</Panel> : null}
        {!overview && !error ? (
          <Panel className="grid place-items-center py-12">
            <Spinner label="Reading users and sessions" />
          </Panel>
        ) : null}
        {overview ? <Overview data={overview} compare={compare} /> : null}

        <section className="space-y-3">
          <SectionHead title="Google Analytics" note={ga ? gaZoneNote(ga.timeZone, range.tz) : undefined} />
          {gaError ? (
            <Panel className="space-y-1 text-sm">
              <p className="text-[color:var(--color-state-slouch)]">{gaError}</p>
              <p className="text-xs text-muted">
                Needs ga-reader@posture-fe600.iam.gserviceaccount.com as a Viewer on GA4 property 555713122.
              </p>
            </Panel>
          ) : !ga ? (
            <Panel className="grid place-items-center py-10">
              <Spinner label="Asking GA4" />
            </Panel>
          ) : (
            <GaSection data={ga} compare={compare} />
          )}
        </section>
      </div>
    </div>
  );
}

/**
 * GA buckets days in the property's own reporting time zone, set in GA Admin,
 * not in the viewer's. Say so when the two differ ("Etc/GMT-9" is UTC+9 — the
 * IANA sign is inverted).
 */
function gaZoneNote(gaZone: string, viewerZone: string): string {
  const etc = /^Etc\/GMT([+-])(\d+)$/.exec(gaZone);
  const readable = etc ? `UTC${etc[1] === '-' ? '+' : '−'}${etc[2]}` : gaZone;
  if (gaZone === viewerZone) return `Days in ${viewerZone}.`;
  return `Shown on your days (${viewerZone}). GA itself counts whole days in ${readable}, so a day's edges can be off by the time difference.`;
}

// ── building blocks ──────────────────────────────────────────────────────────

function Panel({ children, className = '' }: { children: ReactNode; className?: string }): ReactNode {
  return <div className={`min-w-0 rounded-2xl border border-line bg-white p-4 md:p-5 ${className}`}>{children}</div>;
}

function SectionHead({ title, note }: { title: string; note?: string | undefined }): ReactNode {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <h2 className="text-lg font-bold">{title}</h2>
      {note ? <p className="text-xs text-muted">{note}</p> : null}
    </div>
  );
}

function Delta({ now, before, invert = false }: { now: number | null; before: number | null; invert?: boolean }): ReactNode {
  if (now === null || before === null) return null;
  if (before === 0 && now === 0) return <span className="text-xs text-muted">no change</span>;
  if (before === 0) return <span className="text-xs font-semibold text-[color:var(--color-state-good)]">new</span>;
  const pct = Math.round(((now - before) / before) * 100);
  const good = invert ? pct < 0 : pct > 0;
  const color = pct === 0 ? 'text-muted' : good ? 'text-[color:var(--color-state-good)]' : 'text-[color:var(--color-state-slouch)]';
  return (
    <span className={`text-xs font-semibold tabular-nums ${color}`}>
      {pct > 0 ? '▲' : pct < 0 ? '▼' : ''} {Math.abs(pct)}%
    </span>
  );
}

/** Whole numbers stay whole; anything else shows one decimal. */
const num = (v: number): string => (Number.isInteger(v) ? String(v) : v.toFixed(1));

function Kpi({
  label,
  value,
  previous,
  compare,
  suffix = '',
  hint,
  display,
}: {
  label: string;
  value: number | null;
  previous?: number | null;
  compare: boolean;
  suffix?: string;
  hint?: string;
  /** Text shown instead of a number (e.g. "2 · 4 · 7"). */
  display?: string;
}): ReactNode {
  return (
    <Panel className="space-y-1 p-3 md:p-4">
      <p className="text-xs font-medium text-muted">{label}</p>
      <p className="text-2xl font-bold tabular-nums">
        {display ?? (value === null ? '—' : `${num(value)}${suffix}`)}
      </p>
      <div className="flex items-center gap-2 text-xs text-muted">
        {compare && previous !== undefined ? (
          <>
            <Delta now={value} before={previous} />
            <span className="tabular-nums">was {previous === null ? '—' : `${num(previous)}${suffix}`}</span>
          </>
        ) : hint ? (
          <span>{hint}</span>
        ) : null}
      </div>
    </Panel>
  );
}

/** A responsive line chart: the current period solid, the previous dashed underneath, aligned by day. */
function TrendChart({
  title,
  days,
  current,
  previous,
  compare,
  extra,
}: {
  title: string;
  days: string[];
  current: number[];
  previous?: number[];
  compare: boolean;
  extra?: Array<{ label: string; values: number[]; color: string }>;
}): ReactNode {
  const series = [current, ...(compare && previous ? [previous] : []), ...(extra?.map((e) => e.values) ?? [])];
  const max = Math.max(1, ...series.flat());
  const n = Math.max(1, days.length);
  const x = (i: number): number => (n === 1 ? 50 : (i / (n - 1)) * 100);
  const y = (v: number): number => 100 - (v / max) * 92 - 4;
  const path = (values: number[]): string => values.slice(0, n).map((v, i) => `${x(i)},${y(v)}`).join(' ');
  const total = Math.round(current.reduce((s, v) => s + v, 0));
  const prevTotal = previous ? Math.round(previous.reduce((s, v) => s + v, 0)) : null;
  const label = (d: string): string => fromIso(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

  return (
    <Panel className="space-y-2">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-sm font-semibold">{title}</p>
        <p className="flex items-baseline gap-2 text-sm">
          <span className="font-bold tabular-nums">{total}</span>
          {compare && prevTotal !== null ? <Delta now={total} before={prevTotal} /> : null}
        </p>
      </div>
      <div className="relative h-36 md:h-44">
        <div className="absolute inset-0 flex flex-col justify-between">
          {[max, Math.round(max / 2), 0].map((v) => (
            <div key={v} className="flex items-center gap-2">
              <span className="w-6 text-right text-[10px] tabular-nums text-muted">{v}</span>
              <div className="h-px flex-1 bg-line" />
            </div>
          ))}
        </div>
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-y-0 left-8 right-0 h-full w-[calc(100%-2rem)]" role="img" aria-label={`${title} over the period`}>
          {compare && previous ? (
            <polyline points={path(previous)} fill="none" stroke="var(--color-muted)" strokeWidth={1.5} strokeDasharray="4 3" vectorEffect="non-scaling-stroke" />
          ) : null}
          {extra?.map((e) => (
            <polyline key={e.label} points={path(e.values)} fill="none" stroke={e.color} strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
          ))}
          <polyline points={path(current)} fill="none" stroke="var(--color-ink)" strokeWidth={2.2} strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
          {current.map((v, i) => (
            <rect key={i} x={x(i) - 50 / n} y={0} width={100 / n} height={100} fill="transparent">
              <title>{`${label(days[i] ?? '')}: ${v}${compare && previous ? ` (previous ${previous[i] ?? 0})` : ''}`}</title>
            </rect>
          ))}
        </svg>
      </div>
      <div className="flex justify-between pl-8 text-[10px] text-muted">
        <span>{days[0] ? label(days[0]) : ''}</span>
        {days.length > 2 ? <span>{label(days[Math.floor(days.length / 2)]!)}</span> : null}
        <span>{days.length > 1 ? label(days[days.length - 1]!) : ''}</span>
      </div>
      {extra && extra.length > 0 ? (
        <div className="flex flex-wrap gap-3 text-[11px] text-muted">
          {extra.map((e) => (
            <span key={e.label} className="flex items-center gap-1">
              <span className="h-0.5 w-3" style={{ backgroundColor: e.color }} />
              {e.label}
            </span>
          ))}
        </div>
      ) : null}
    </Panel>
  );
}

function BarList({ rows, unit = '' }: { rows: Array<{ label: string; value: number; note?: string }>; unit?: string }): ReactNode {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <ul className="space-y-1.5">
      {rows.map((r) => (
        <li key={r.label} className="grid grid-cols-[minmax(0,10rem)_1fr_auto] items-center gap-2 text-xs">
          <span className="truncate">{r.label}</span>
          <span className="h-2.5 overflow-hidden rounded-full bg-surface">
            <span className="block h-full rounded-full bg-ink/70" style={{ width: `${(r.value / max) * 100}%` }} />
          </span>
          <span className="tabular-nums text-muted">
            {r.value}
            {unit}
            {r.note ? ` · ${r.note}` : ''}
          </span>
        </li>
      ))}
    </ul>
  );
}

// ── our data ─────────────────────────────────────────────────────────────────

const pct = (part: number, whole: number): string => (whole ? `${Math.round((part / whole) * 100)}%` : '—');

function Overview({ data, compare }: { data: AdminOverview; compare: boolean }): ReactNode {
  const t = data.current.totals;
  const p = data.previous.totals;
  const days = data.current.daily.map((d) => d.day);
  const series = (k: keyof Omit<AdminDay, 'day'>, period = data.current): number[] => period.daily.map((d) => d[k]);

  return (
    <>
      <section className="space-y-3">
        <SectionHead title="Usage" note={`Days in ${data.tz}`} />
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
          <Kpi label="Active users" value={t.activeUsers} previous={p.activeUsers} compare={compare} />
          <Kpi label="Returning users" value={t.returningUsers} previous={p.returningUsers} compare={compare} hint={pct(t.returningUsers, t.activeUsers) + ' of active'} />
          <Kpi label="New active users" value={t.newActiveUsers} previous={p.newActiveUsers} compare={compare} />
          <Kpi label="Sign-ups" value={t.signups} previous={p.signups} compare={compare} />
          <Kpi label="Avg daily active" value={t.avgDailyActive} previous={p.avgDailyActive} compare={compare} />
          <Kpi label="Sessions" value={t.sessions} previous={p.sessions} compare={compare} />
          <Kpi label="Session minutes" value={t.sessionMinutes} previous={p.sessionMinutes} compare={compare} />
          <Kpi label="Avg session length" value={t.avgSessionMin} previous={p.avgSessionMin} compare={compare} suffix=" min" />
          <Kpi label="Sessions per active user" value={t.sessionsPerActive} previous={p.sessionsPerActive} compare={compare} />
          <Kpi label="Avg session score" value={t.avgScore} previous={p.avgScore} compare={compare} />
          <Kpi label="Posture checks" value={t.checks} previous={p.checks} compare={compare} />
          <Kpi label="Exercises done" value={t.exercisesDone} previous={p.exercisesDone} compare={compare} />
          <Kpi label="Darwin questions" value={t.coachQuestions} previous={p.coachQuestions} compare={compare} />
          <Kpi label="DAU · WAU · MAU" value={null} compare={false} display={`${data.reach.dau} · ${data.reach.wau} · ${data.reach.mau}`} hint="Distinct users in the last 1 / 7 / 30 days" />
          <Kpi label="Stickiness (DAU ÷ MAU)" value={data.reach.stickiness === null ? null : Math.round(data.reach.stickiness * 100)} suffix="%" compare={false} hint="How many monthly users show up on a given day" />
          <Kpi label="Accounts (all time)" value={data.allTime.users} compare={false} hint={`${data.allTime.pushEnabled} with push · ${data.allTime.sharingOptIn} sharing`} />
        </div>
      </section>

      <section className="space-y-3">
        <SectionHead title="Trends" note={compare ? 'Solid: this period · dashed: the previous one' : undefined} />
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          <TrendChart title="Active users" days={days} current={series('activeUsers')} previous={series('activeUsers', data.previous)} compare={compare} />
          <TrendChart title="Sessions" days={days} current={series('sessions')} previous={series('sessions', data.previous)} compare={compare} />
          <TrendChart title="Session minutes" days={days} current={series('minutes')} previous={series('minutes', data.previous)} compare={compare} />
          <TrendChart title="Sign-ups" days={days} current={series('signups')} previous={series('signups', data.previous)} compare={compare} />
          <TrendChart title="Posture checks" days={days} current={series('checks')} previous={series('checks', data.previous)} compare={compare} />
          <TrendChart title="Exercises done" days={days} current={series('exercises')} previous={series('exercises', data.previous)} compare={compare} />
          <TrendChart title="Darwin questions" days={days} current={series('coach')} previous={series('coach', data.previous)} compare={compare} />
        </div>
      </section>

      <section className="grid gap-3 lg:grid-cols-3">
        <Panel className="space-y-3">
          <p className="text-sm font-semibold">Funnel · signed up in this period</p>
          <FunnelBars f={data.funnel.period} />
        </Panel>
        <Panel className="space-y-3">
          <p className="text-sm font-semibold">Funnel · everyone</p>
          <FunnelBars f={data.funnel.allTime} />
        </Panel>
        <Panel className="space-y-3">
          <p className="text-sm font-semibold">Coming back</p>
          <BarList
            rows={data.retention.map((r) => ({
              label: r.label,
              value: r.eligible ? Math.round((r.returned / r.eligible) * 100) : 0,
              note: `${r.returned} of ${r.eligible}`,
            }))}
            unit="%"
          />
          <p className="text-[11px] text-muted">
            Of people whose first activity was long enough ago: how many were active again later.
          </p>
        </Panel>
      </section>

      <section className="grid gap-3 lg:grid-cols-2">
        <Panel className="space-y-2">
          <p className="text-sm font-semibold">When sessions start · hour of day</p>
          <Histogram values={data.when.hours} labels={data.when.hours.map((_, h) => (h % 6 === 0 ? `${h}h` : ''))} />
        </Panel>
        <Panel className="space-y-2">
          <p className="text-sm font-semibold">When sessions start · day of week</p>
          <Histogram values={data.when.weekdays} labels={['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']} />
        </Panel>
      </section>

      <CaptureHealth data={data} />
      <UsersTable users={data.users} />
    </>
  );
}

function FunnelBars({ f }: { f: AdminFunnel }): ReactNode {
  const steps: Array<[string, number]> = [
    ['Signed up', f.signedUp],
    ['Finished onboarding', f.onboarded],
    ['First session', f.firstSession],
    ['Two or more sessions', f.repeatSessions],
    ['Did a posture check', f.postureCheck],
    ['Did an exercise', f.exercise],
    ['Asked Darwin', f.darwin],
  ];
  return <BarList rows={steps.map(([label, value]) => ({ label, value, note: pct(value, f.signedUp) }))} />;
}

function Histogram({ values, labels }: { values: number[]; labels: string[] }): ReactNode {
  const max = Math.max(1, ...values);
  return (
    <div>
      <div className="flex h-28 items-end gap-0.5">
        {values.map((v, i) => (
          <div key={i} className="flex-1 rounded-t bg-ink/70" style={{ height: `${v ? Math.max(4, (v / max) * 100) : 0}%` }} title={`${labels[i] || i}: ${v}`} />
        ))}
      </div>
      <div className="mt-1 flex gap-0.5 text-[10px] text-muted">
        {labels.map((l, i) => (
          <span key={i} className="flex-1 text-center">
            {l}
          </span>
        ))}
      </div>
    </div>
  );
}

function CaptureHealth({ data }: { data: AdminOverview }): ReactNode {
  const c = data.capture;
  return (
    <section className="space-y-3">
      <SectionHead title="Capture health" note="Is the fixed sample rate kept, and how good is the picture?" />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
        <Kpi label="Median actual fps" value={c.medianActualFps} compare={false} />
        <Kpi label="Slow sessions" value={c.slowSessions} compare={false} hint={`of ${c.sessions}`} />
        <Kpi label="Median measurable" value={c.medianValidRatio === null ? null : Math.round(c.medianValidRatio * 100)} suffix="%" compare={false} />
        <Kpi label="Median phone roll" value={c.medianRollDeg} suffix="°" compare={false} />
        <Kpi label="Median phone lean" value={c.medianPitchDeg} suffix="°" compare={false} />
        <Kpi label="Pose model" value={null} compare={false} display={Object.entries(c.byModel).map(([k, v]) => `${k} ${v}`).join(' · ') || '—'} hint="Sessions per model" />
      </div>
      {c.slow.length > 0 ? (
        <Panel>
          <p className="mb-2 text-sm font-semibold">Sessions that missed the rate</p>
          <ul className="space-y-1 text-xs tabular-nums">
            {c.slow.map((s) => (
              <li key={`${s.uid}-${s.at}`}>
                {new Date(s.at).toLocaleString()} · {s.uid.slice(0, 8)} · {s.actualFps} of {s.sampleFps} fps · {s.deviceClass} device
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}
    </section>
  );
}

type SortKey = 'email' | 'createdAt' | 'lastSignInAt' | 'sessions' | 'sessionMinutes' | 'avgScore' | 'lastSessionAt' | 'checks' | 'exercisesDone' | 'coachQuestions' | 'stage';

const PAGE_SIZE = 10;

function UsersTable({ users }: { users: AdminUserRow[] }): ReactNode {
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: 'lastSessionAt', desc: true });
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const sorted = useMemo(() => {
    const value = (u: AdminUserRow): string | number => {
      const v = u[sort.key];
      return typeof v === 'string' ? v : (v ?? -1);
    };
    return [...users].sort((a, b) => {
      const va = value(a);
      const vb = value(b);
      const cmp = typeof va === 'string' && typeof vb === 'string' ? va.localeCompare(vb) : Number(va) - Number(vb);
      return sort.desc ? -cmp : cmp;
    });
  }, [sort, users]);
  // Search by email, uid, sign-in method or time zone.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter((u) =>
      [u.email ?? '', u.uid, u.providers.join(' '), u.tz ?? ''].some((f) => f.toLowerCase().includes(q)),
    );
  }, [query, sorted]);
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, pages - 1);
  const rows = filtered.slice(current * PAGE_SIZE, (current + 1) * PAGE_SIZE);
  const date = (ms: number | null): string => (ms ? new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '—');
  const cols: Array<[SortKey, string]> = [
    ['email', 'User'],
    ['createdAt', 'Joined'],
    ['lastSignInAt', 'Last sign-in'],
    ['lastSessionAt', 'Last active'],
    ['sessions', 'Sessions'],
    ['sessionMinutes', 'Minutes'],
    ['avgScore', 'Avg score'],
    ['checks', 'Checks'],
    ['exercisesDone', 'Exercises'],
    ['coachQuestions', 'Darwin'],
    ['stage', 'Stage'],
  ];

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <SectionHead title={`Users (${users.length})`} note="All time · click a column to sort" />
        <input
          id="admin-user-search"
          type="search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setPage(0);
          }}
          placeholder="Search email, uid, sign-in, time zone"
          className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm sm:w-80"
        />
      </div>
      <Panel className="overflow-x-auto p-0 md:p-0">
        <table className="w-full min-w-[900px] text-left text-sm tabular-nums">
          <thead className="border-b border-line bg-surface text-xs text-muted">
            <tr>
              {cols.map(([key, label]) => (
                <th key={key} className="px-3 py-2 font-medium">
                  <button type="button" onClick={() => setSort((s) => ({ key, desc: s.key === key ? !s.desc : true }))} className="hover:text-ink">
                    {label}
                    {sort.key === key ? (sort.desc ? ' ↓' : ' ↑') : ''}
                  </button>
                </th>
              ))}
              <th className="px-3 py-2 font-medium">Push</th>
              <th className="px-3 py-2 font-medium">Sharing</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.uid} className="border-b border-line last:border-0 hover:bg-surface/60">
                <td className="px-3 py-2">
                  {u.email ?? u.uid.slice(0, 8)}
                  {u.admin ? <span className="ml-1 text-xs text-muted">· team</span> : null}
                  <span className="block text-[11px] text-muted">{u.providers.join(', ') || 'email link'}{u.tz ? ` · ${u.tz}` : ''}</span>
                </td>
                <td className="px-3 py-2">{date(u.createdAt)}</td>
                <td className="px-3 py-2">{date(u.lastSignInAt)}</td>
                <td className="px-3 py-2">{date(u.lastSessionAt)}</td>
                <td className="px-3 py-2">{u.sessions}</td>
                <td className="px-3 py-2">{u.sessionMinutes}</td>
                <td className="px-3 py-2">{u.avgScore ?? '—'}</td>
                <td className="px-3 py-2">{u.checks}</td>
                <td className="px-3 py-2">{u.exercisesDone}</td>
                <td className="px-3 py-2">{u.coachQuestions}</td>
                <td className="px-3 py-2">{u.stage ?? '—'}</td>
                <td className="px-3 py-2">{u.pushTokens > 0 ? 'on' : '—'}</td>
                <td className="px-3 py-2">{u.sharing ? 'yes' : '—'}</td>
              </tr>
            ))}
            {rows.length === 0 ? (
              <tr>
                <td colSpan={13} className="px-3 py-6 text-center text-sm text-muted">
                  No user matches “{query}”.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </Panel>
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <span className="text-muted tabular-nums">
          {filtered.length === 0 ? 0 : current * PAGE_SIZE + 1}–{Math.min((current + 1) * PAGE_SIZE, filtered.length)} of {filtered.length}
          {query ? ` matching · ${users.length} total` : ''}
        </span>
        <div className="flex items-center gap-1">
          <PageButton label="First" disabled={current === 0} onClick={() => setPage(0)} />
          <PageButton label="‹ Prev" disabled={current === 0} onClick={() => setPage(current - 1)} />
          <span className="px-2 tabular-nums">
            {current + 1} / {pages}
          </span>
          <PageButton label="Next ›" disabled={current >= pages - 1} onClick={() => setPage(current + 1)} />
          <PageButton label="Last" disabled={current >= pages - 1} onClick={() => setPage(pages - 1)} />
        </div>
      </div>
    </section>
  );
}

function PageButton({ label, disabled, onClick }: { label: string; disabled: boolean; onClick(): void }): ReactNode {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="rounded-lg border border-line bg-white px-2.5 py-1 text-xs font-semibold disabled:opacity-40"
    >
      {label}
    </button>
  );
}

// ── GA ───────────────────────────────────────────────────────────────────────

const PLATFORM_COLOR: Record<string, string> = { iOS: '#2563eb', Android: '#16a34a', web: '#d97706' };

function GaSection({ data, compare }: { data: AdminGa; compare: boolean }): ReactNode {
  const days = [...new Set(data.daily.map((d) => d.day))].sort();
  const platforms = [...new Set(data.daily.map((d) => d.platform))];
  const total = days.map((day) => data.daily.filter((d) => d.day === day).reduce((s, d) => s + d.activeUsers, 0));
  const prevDays = [...new Set(data.previousDaily.map((d) => d.day))].sort();
  const prev = prevDays.map((day) => data.previousDaily.filter((d) => d.day === day).reduce((s, d) => s + d.activeUsers, 0));
  const c = data.current;
  const p = data.previous;
  const nvr = data.newVsReturning;
  const totalEngaged = data.screens.reduce((s, x) => s + x.engagementMin, 0);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7">
        <Kpi label="Active users" value={c.activeUsers} previous={p.activeUsers} compare={compare} />
        <Kpi label="New users" value={c.newUsers} previous={p.newUsers} compare={compare} />
        <Kpi label="Returning users" value={nvr.current.returning} previous={nvr.previous.returning} compare={compare} hint={pct(nvr.current.returning, nvr.current.new + nvr.current.returning) + ' of active'} />
        <Kpi label="App sessions" value={c.sessions} previous={p.sessions} compare={compare} />
        <Kpi label="Engaged minutes" value={c.engagementMin} previous={p.engagementMin} compare={compare} />
        <Kpi label="Engaged min per user" value={c.activeUsers ? Math.round((c.engagementMin / c.activeUsers) * 10) / 10 : null} previous={p.activeUsers ? Math.round((p.engagementMin / p.activeUsers) * 10) / 10 : null} compare={compare} />
        <Kpi label="Events" value={c.events} previous={p.events} compare={compare} />
      </div>

      <div className="grid gap-3 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <TrendChart
            title="Active users (GA)"
            days={days}
            current={total}
            previous={prev}
            compare={compare}
            extra={platforms.map((pl) => ({
              label: pl,
              color: PLATFORM_COLOR[pl] ?? '#64748b',
              values: days.map((day) => data.daily.find((d) => d.day === day && d.platform === pl)?.activeUsers ?? 0),
            }))}
          />
        </div>
        <Panel className="space-y-3">
          <p className="text-sm font-semibold">Platforms</p>
          <BarList rows={data.platforms.map((pl) => ({ label: pl.platform, value: pl.users, note: compare ? `was ${pl.previous}` : undefined }))} />
          <p className="pt-2 text-sm font-semibold">Countries</p>
          <BarList rows={data.countries.map((co) => ({ label: co.country, value: co.users }))} />
        </Panel>
      </div>

      <Panel className="space-y-3">
        <div className="flex items-baseline justify-between">
          <p className="text-sm font-semibold">Where people spend time</p>
          <p className="text-xs text-muted">Engaged minutes by screen · {Math.round(totalEngaged)} min total</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-left text-sm tabular-nums">
            <thead className="text-xs text-muted">
              <tr>
                <th className="py-1 pr-3 font-medium">Screen</th>
                <th className="py-1 pr-3 font-medium">Engaged min</th>
                <th className="py-1 pr-3 font-medium">Share</th>
                <th className="py-1 pr-3 font-medium">Min per user</th>
                <th className="py-1 pr-3 font-medium">Views</th>
                <th className="py-1 pr-3 font-medium">Users</th>
                {compare ? <th className="py-1 pr-3 font-medium">vs previous</th> : null}
              </tr>
            </thead>
            <tbody>
              {data.screens.map((s) => (
                <tr key={s.screen} className="border-t border-line">
                  <td className="py-1.5 pr-3 font-medium">{s.screen}</td>
                  <td className="pr-3">
                    <span className="flex items-center gap-2">
                      <span className="inline-block h-2 rounded-full bg-ink/70" style={{ width: `${totalEngaged ? Math.max(2, (s.engagementMin / totalEngaged) * 160) : 0}px` }} />
                      {s.engagementMin}
                    </span>
                  </td>
                  <td className="pr-3">{pct(s.engagementMin, totalEngaged)}</td>
                  <td className="pr-3">{s.users ? Math.round((s.engagementMin / s.users) * 10) / 10 : '—'}</td>
                  <td className="pr-3">{s.views}</td>
                  <td className="pr-3">{s.users}</td>
                  {compare ? (
                    <td className="pr-3">
                      <Delta now={s.engagementMin} before={s.previousEngagementMin} />
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <Panel className="space-y-2">
        <p className="text-sm font-semibold">Events</p>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[520px] text-left text-sm tabular-nums">
            <thead className="text-xs text-muted">
              <tr>
                <th className="py-1 pr-3 font-medium">Event</th>
                <th className="py-1 pr-3 font-medium">Count</th>
                <th className="py-1 pr-3 font-medium">Users</th>
                {compare ? (
                  <>
                    <th className="py-1 pr-3 font-medium">Previous</th>
                    <th className="py-1 pr-3 font-medium">Change</th>
                  </>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {data.events.map((e) => (
                <tr key={e.name} className="border-t border-line">
                  <td className="py-1.5 pr-3 font-mono text-xs">{e.name}</td>
                  <td className="pr-3">{e.count}</td>
                  <td className="pr-3">{e.users}</td>
                  {compare ? (
                    <>
                      <td className="pr-3 text-muted">{e.previous}</td>
                      <td className="pr-3">
                        <Delta now={e.count} before={e.previous} />
                      </td>
                    </>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
